/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing
 * the entire application.
 *
 * USAGE:
 * Wrap the root App component to prevent full app crashes:
 * <ErrorBoundary><App /></ErrorBoundary>
 *
 * @component
 */
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Sentry } from '../sentry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console and report to Sentry (no-op if Sentry not initialized).
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    Sentry.withScope(scope => {
      scope.setExtra('componentStack', errorInfo.componentStack);
      Sentry.captureException(error);
    });
  }

  handleReload = () => {
    // Clear any corrupted state and reload
    sessionStorage.removeItem('goacto-timer');
    sessionStorage.removeItem('goacto-timer-topic');
    window.location.reload();
  };

  handleReset = () => {
    // Try to recover without full reload
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div className="min-h-screen bg-dark text-gold flex flex-col items-center justify-center p-6">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-6">😵</div>
            <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
            <p className="text-gray-400 mb-6">
              The app encountered an unexpected error. Your data is safe - try refreshing the page.
            </p>

            {/* Error details (collapsed by default) */}
            <details className="mb-6 text-left">
              <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-300">
                Technical details
              </summary>
              <pre className="mt-2 p-3 bg-gray-800 rounded-lg text-xs overflow-auto max-h-32 text-red-400">
                {this.state.error?.message || 'Unknown error'}
              </pre>
            </details>

            <div className="space-y-3">
              <button
                onClick={this.handleReload}
                className="w-full py-3 bg-gold text-dark font-bold rounded-xl hover:scale-105 transition"
              >
                Reload App
              </button>
              <button
                onClick={this.handleReset}
                className="w-full py-3 bg-gray-700 text-gold font-bold rounded-xl hover:bg-gray-600 transition"
              >
                Try to Continue
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
